/**
 * THE BOARDS.
 *
 * This is not a leaderboard with a theme on it. It is the four printed boards of
 * Red White & Blue Racin' — LEADER, TOP, MIDDLE, BOTTOM — with the driver cards
 * sitting in their slots, and it moves the way a hand moves cards on a table.
 *
 * HOW THE MOVEMENT WORKS. Every card is one absolutely-positioned element that
 * is keyed on `driverId` and never unmounts while the driver is running, and its
 * DOM order is fixed (sorted by id, not by position). Its slot is expressed only
 * as a `translate3d`. So when the engine hands us a new running order, React
 * changes a transform on an element that is already in the document and the
 * browser tweens it — a card physically slides from 7th to 2nd. Nothing here
 * re-orders the DOM, and nothing here decides where a card goes: `slotFor` turns
 * the engine's `position` into coordinates and that is the whole of it.
 *
 * Nothing in this file computes a rule. Chips, positions, retirements, who was
 * collected — all of it arrives as engine output.
 *
 * THE CARD FACE follows the printed card top to bottom: the car seen FROM
 * ABOVE with its number on the roof and its make on the nose tab, the driver's
 * name in small caps, the gas pedal as solid squares and the brake pedal as
 * stars, the fixed 3x2 quality grid with its ★ welded to the quality it belongs
 * to, and the pit crew grade printed inside a gold, silver or bronze trophy
 * with the metal named underneath — or bare, no trophy at all, when the driver
 * has not won one.
 *
 * THE ONE DEVIATION is colour. The printed car is black ink; ours is painted in
 * its team's livery, and the FAITHFUL toggle above the boards puts every car
 * back in printed black. Colour is decoration only — no rule anywhere reads it,
 * and everything it says is also said in text.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { Car, PitCrewGrade, Quality, RaceState, Trophy } from '../domain/types.ts';
import { carQualities, isStarred } from '../domain/types.ts';
import type { Team } from '../domain/field.ts';
import type { Livery } from '../domain/livery.ts';
import { PRINTED_LIVERY, asPrinted, liveryForDriver } from '../domain/livery.ts';
import CarTopDown from './CarTopDown.tsx';
import { logoForTeam, useLogos, type StoredLogo } from '../lib/logoStore.ts';
import type { TurnResult } from '../domain/turn.ts';
import {
  BOARD_H,
  BOARD_LEFT_W,
  BOARD_W,
  BOX_H,
  BOX_W,
  CARD_W,
  HEADING_H,
  LEADER_X,
  LEADER_Y,
  MIDDLE_BOXES,
  MIDDLE_Y,
  PRINTED_SLOTS,
  RAIL_X,
  RAIL_Y,
  TOP_Y,
  bucketField,
  middleBoxOrigin,
  ordinal,
  slotFor,
} from './boardModel.ts';
import type { Flag } from './boardModel.ts';
import { moveArrows, pathOf } from './moveArrows.ts';
import type { MoveArrow } from './moveArrows.ts';
import { TRAITS_BY_CODE } from '../domain/traits.ts';
import { useCiteText } from '../lib/prefs.ts';

/** D-EZ-101 — the smallest the board is drawn; below this the frame scrolls sideways. */
export const MIN_READABLE_SCALE = 0.66;

/** The printed 3x2 quality grid: left column REFLEX/CONTROL/SKILL, right STAR/SMART/HEAT. */
export const QUALITY_GRID: readonly Quality[] = ['REFLEX', 'STAR', 'CONTROL', 'SMART', 'SKILL', 'HEAT'];

/**
 * How wide the car is drawn on a card, in px.
 *
 * The plan-view car is 2:1, so its WIDTH sets its HEIGHT — drawn to the full
 * 104px of a 118px card it would stand 52px tall and push the quality grid off
 * the bottom. These are the widths whose halves fit the band the printed card
 * gives the car, which is why they are constants rather than `slot.w` minus a
 * guess.
 */
const CARD_CAR_W = 86;
const LEADER_CAR_W = 118;

/** Where the faithful/coloured choice is remembered between sessions. */
export const LIVERY_PREF_KEY = 'rwbr.liveries';
/** …and whether the move arrows are drawn. D-EZ-58. */
const ARROWS_PREF_KEY = 'rwbr.movearrows';

export interface RaceBoardsProps {
  /** The running order to draw. */
  state: RaceState;
  /** Where the cards were before this turn — used to lift a wrecked car out of its own slot. */
  previous: RaceState | null;
  /** The turn just played, or null before the green. */
  turn: TurnResult | null;
  /** Drivers the selected ledger event is about. */
  highlighted: readonly string[];
  flag: Flag;
  /**
   * The garage, when the caller has it. Used for two things and no others: the
   * shop's name in the screen-reader running order, and a driver's slot on his
   * team's roster, which is what keeps three teammates in three different
   * paints. Without it the cars are still painted, off their driver ids.
   */
  teams?: readonly Team[];
  /**
   * UI-008 — open a driver's dossier. Omit it and the cards are inert, exactly
   * as they were.
   */
  onSelectDriver?: (driverId: string) => void;
}

export default function RaceBoards({
  state,
  previous,
  turn,
  highlighted,
  flag,
  teams,
  onSelectDriver,
}: RaceBoardsProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  /**
   * D-EZ-101 — on a phone the table can be read as the board (scrolled
   * sideways inside its frame, never shrunk to thumbnails) or as the running
   * order in words. The switch is only drawn on narrow screens (CSS).
   */
  const [phoneView, setPhoneView] = useState<'board' | 'order'>('board');

  // Colour is on by default; the preference is restored in an effect so that
  // server rendering and the first paint agree.
  const [colour, setColour] = useState(true);
  useEffect(() => {
    try {
      if (window.localStorage.getItem(LIVERY_PREF_KEY) === 'printed') setColour(false);
    } catch {
      /* private mode, wiped storage — the default stands. */
    }
  }, []);

  /**
   * D-EZ-58 — the move arrows, and the box that turns them off.
   *
   * Howard, 18 Aug 2026: *"the box is to check the arrows to be visible or not
   * each turn."* On by default, because the first thing anyone wants from a
   * board that has just moved is to see WHAT moved; remembered, because a
   * person who turns them off means it, and re-reading the same preference
   * every turn is the whole point of a box rather than a gesture.
   */
  const [arrows, setArrows] = useState(true);
  useEffect(() => {
    try {
      if (window.localStorage.getItem(ARROWS_PREF_KEY) === 'off') setArrows(false);
    } catch {
      /* the default stands */
    }
  }, []);

  const roster = useMemo(() => {
    const map = new Map<string, { team: Team; index: number }>();
    for (const team of teams ?? []) {
      team.driverIds.forEach((id, index) => map.set(id, { team, index }));
    }
    return map;
  }, [teams]);

  const liveries = useMemo(() => {
    const map = new Map<string, Livery>();
    for (const car of [...state.grid, ...state.retired]) {
      const card = car.card;
      const seat = roster.get(card.driverId);
      // `seat.team.baseInk` is the colour this shop was DEALT for this field.
      // Drop it and the car falls back to the team id's hashed colour, which is
      // where the six-purple-cars problem came from.
      map.set(
        card.driverId,
        liveryForDriver(card.driverId, card.teamId, seat?.index, seat?.team.baseInk, card.make),
      );
    }
    return map;
  }, [state.grid, state.retired, roster]);

  // In printed mode the car goes back to black ink — but keeps its make, since
  // the manufacturer tab is printed on the physical card too.
  /**
   * The shop's mark for a car's hood, by the TEAM'S NAME.
   *
   * The teams prop is optional on this component, and a board with no garage
   * simply shows plain hoods — the same graceful absence as a shop that has not
   * uploaded a mark. Nothing here can fail loudly.
   */
  const logos = useLogos();
  const hoodLogoOf = (driverId: string): StoredLogo | null =>
    logoForTeam(logos, roster.get(driverId)?.team.name ?? null);

  const liveryOf = (driverId: string): Livery => {
    const painted = liveries.get(driverId) ?? PRINTED_LIVERY;
    return colour ? painted : asPrinted(painted);
  };

  // Fit the table to whatever room the window gives it. Layout is authored in
  // board units and scaled as one piece, so the boards never reflow.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof ResizeObserver === 'undefined') return;
    const fit = () => {
      const w = host.clientWidth;
      // D-EZ-101 — never below a readable scale: a narrow frame scrolls the
      // board sideways instead (`.board-host` overflows on phones). Desktop
      // widths are untouched — the floor only bites below 680px of frame.
      if (w > 0) setScale(Math.min(1, Math.max(MIN_READABLE_SCALE, w / BOARD_W)));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(host);
    return () => ro.disconnect();
  }, []);

  const order = useMemo(() => [...state.grid].sort((a, b) => a.position - b.position), [state.grid]);
  const boards = useMemo(() => bucketField(order), [order]);

  // Stable DOM order so React mutates transforms instead of moving nodes.
  const painted = useMemo(
    () => [...state.grid].sort((a, b) => a.card.driverId.localeCompare(b.card.driverId)),
    [state.grid],
  );

  const collected = useMemo(() => new Set(turn?.collected ?? []), [turn]);
  const highlight = useMemo(() => new Set(highlighted), [highlighted]);

  /**
   * Cars that left the race on this turn, drawn one last time in the slot they
   * occupied when the turn began, lifting out of the grid.
   */
  const lifted = useMemo(() => {
    if (!turn || turn.retiredThisTurn.length === 0) return [];
    return turn.retiredThisTurn
      .map((id) => {
        const car = state.retired.find((c) => c.card.driverId === id);
        if (!car) return null;
        const before = previous?.grid.find((c) => c.card.driverId === id)?.position;
        return { car, position: before ?? car.position };
      })
      .filter((x): x is { car: Car; position: number } => x !== null);
  }, [turn, state.retired, previous]);

  /**
   * ⚠ COMPUTED WHATEVER THE BOX SAYS, and hidden with `display` rather than by
   * not computing it. The tally under the boards is read aloud to anyone using
   * a screen reader, and that should not turn off because a sighted player
   * cleared some lines off their table.
   */
  const movement = useMemo(
    () =>
      moveArrows(
        state.grid.map((c) => ({ driverId: c.card.driverId, position: c.position })),
        previous ? previous.grid.map((c) => ({ driverId: c.card.driverId, position: c.position })) : null,
      ),
    [state.grid, previous],
  );
  const moves = movement.arrows;

  const stackCount = boards.bottom.length;

  return (
    <section className={`boards${phoneView === 'order' ? ' is-order' : ''}`} aria-labelledby="boards-title">
      <h2 className="sr-only" id="boards-title">
        The boards — LEADER, TOP, MIDDLE and BOTTOM
      </h2>

      <div className="boards__phoneview segmented segmented--tiny" role="group" aria-label="Show the race as">
        <button type="button" className={`seg${phoneView === 'board' ? ' is-on' : ''}`} aria-pressed={phoneView === 'board'} onClick={() => setPhoneView('board')}>
          The board
        </button>
        <button type="button" className={`seg${phoneView === 'order' ? ' is-on' : ''}`} aria-pressed={phoneView === 'order'} onClick={() => setPhoneView('order')}>
          Running order
        </button>
      </div>

      <div className="boards__bar">
        <span className="boards__barlabel">Paint</span>
        <div className="segmented segmented--tiny" role="group" aria-label="How the cars are painted">
          <button
            type="button"
            className={`seg${colour ? ' is-on' : ''}`}
            aria-pressed={colour}
            onClick={() => {
              setColour(true);
              try {
                window.localStorage.setItem(LIVERY_PREF_KEY, 'colour');
              } catch { /* nothing to remember it with */ }
            }}
          >
            Team liveries
          </button>
          <button
            type="button"
            className={`seg${colour ? '' : ' is-on'}`}
            aria-pressed={!colour}
            onClick={() => {
              setColour(false);
              try {
                window.localStorage.setItem(LIVERY_PREF_KEY, 'printed');
              } catch { /* nothing to remember it with */ }
            }}
          >
            As printed
          </button>
        </div>
        <span className="boards__barnote">
          {colour
            ? 'House layer — the printed card is black ink.'
            : 'The card exactly as it prints: black car, white number.'}
        </span>

        {/*
          D-EZ-58 — the box, top right, exactly where Howard asked for it. A
          real checkbox and not a third segmented control: the paint choice is
          one of two states, this is on or off, and the two controls should not
          look like the same kind of decision.
        */}
        <label className="boards__arrowbox">
          <input
            type="checkbox"
            checked={arrows}
            onChange={(e) => {
              setArrows(e.target.checked);
              try {
                window.localStorage.setItem(ARROWS_PREF_KEY, e.target.checked ? 'on' : 'off');
              } catch { /* nothing to remember it with */ }
            }}
          />
          <span>Show the moves</span>
          <span className="boards__arrowcount">
            {/*
              ⚠ THE SUPPRESSED COUNT IS PRINTED, NOT SWALLOWED. Moves inside the
              BOTTOM stack are not drawable — see the header of moveArrows.ts —
              but "12 of them happened and are not on screen" is a fact a player
              is owed, and a silent cap reads as "that was everything".
            */}
            {moves.length === 0 && movement.suppressed === 0
              ? 'nothing moved'
              : `${moves.length} this turn` +
                (movement.suppressed > 0 ? ` · ${movement.suppressed} inside the pack` : '')}
          </span>
        </label>
      </div>

      <div className="board-host" ref={hostRef} style={{ height: BOARD_H * scale }}>
        <div
          className={`board-stage flag-${flag}`}
          style={{ width: BOARD_W, height: BOARD_H, transform: `scale(${scale})` }}
        >
          {/* ---- printed board furniture ------------------------------------ */}
          <BoardHeading x={LEADER_X - 60} y={LEADER_Y - HEADING_H} w={270} text="Race Leader" />
          <BoardHeading x={0} y={TOP_Y - HEADING_H} w={BOARD_LEFT_W} text="Top Group · Board A" />
          <BoardHeading x={0} y={MIDDLE_Y - HEADING_H} w={BOARD_LEFT_W} text="Middle Group · Board B" />
          <BoardHeading
            x={RAIL_X}
            y={RAIL_Y - HEADING_H - 14}
            w={CARD_W + 12}
            text={`Bottom · ${stackCount}`}
          />

          {MIDDLE_BOXES.map(({ box, odd, even }) => {
            const origin = middleBoxOrigin(box);
            return (
              <div
                key={`box-${box}`}
                className="mbox"
                style={{ transform: `translate3d(${origin.x}px, ${origin.y}px, 0)`, width: BOX_W, height: BOX_H }}
              >
                <span className="mbox__die" aria-hidden="true">{box}</span>
                <span className="mbox__half mbox__half--odd">odd · {ordinal(odd)}</span>
                <span className="mbox__half mbox__half--even">even · {ordinal(even)}</span>
              </div>
            );
          })}

          {PRINTED_SLOTS.map((slot) => (
            <div
              key={`slot-${slot.position}`}
              className={`slot slot--${slot.group}`}
              style={{
                transform: `translate3d(${slot.x}px, ${slot.y}px, 0)`,
                width: slot.w,
                height: slot.h,
              }}
            >
              <span className="slot__place">{ordinal(slot.position)}</span>
            </div>
          ))}

          <div
            className="rail"
            style={{
              transform: `translate3d(${RAIL_X - 10}px, ${RAIL_Y - 10}px, 0)`,
              width: CARD_W + 26,
              height: BOARD_H - RAIL_Y,
            }}
            aria-hidden="true"
          />

          {/* ---- the cards --------------------------------------------------- */}
          {painted.map((car) => (
            <DriverCardFace
              key={car.card.driverId}
              car={car}
              livery={liveryOf(car.card.driverId)}
              hoodLogo={hoodLogoOf(car.card.driverId)}
              printed={!colour}
              collected={collected.has(car.card.driverId)}
              highlighted={highlight.has(car.card.driverId)}
              tookTheLead={turn?.leadChangedTo === car.card.driverId}
              onOpen={onSelectDriver}
            />
          ))}

          {/*
            OVER THE CARDS, NOT UNDER THEM. An arm that runs the width of Board
            A passes over four cards on the way; drawn underneath it would
            vanish behind them and read as four unrelated stubs. Over the top,
            thin and translucent, it reads as what it is — an annotation on the
            table rather than a thing on it. `pointer-events: none` keeps every
            card underneath clickable.
          */}
          {arrows && moves.length > 0 && <MoveArrowLayer moves={moves} />}

          {lifted.map(({ car, position }) => (
            <DriverCardFace
              key={`out-${car.card.driverId}`}
              car={car}
              livery={liveryOf(car.card.driverId)}
              hoodLogo={hoodLogoOf(car.card.driverId)}
              printed={!colour}
              overridePosition={position}
              out
              highlighted={highlight.has(car.card.driverId)}
              onOpen={onSelectDriver}
            />
          ))}
        </div>
      </div>

      {/*
        The running order, in running order, for anyone not reading pixels —
        and the reason the paint is allowed to be decoration: the team a car
        belongs to is named here in words, not just implied by its colour.

        UI-008 PUTS THE KEYBOARD DOOR HERE, and that is a deliberate choice.
        A card on the boards is `aria-hidden` — it has to be, or a screen reader
        would read forty-three cards in driver-id order, which is not a running
        order and is not useful to anybody. You cannot un-hide a focusable child
        of an aria-hidden element, so the card's hit area is pointer-only, and
        the accessible way in is this list: it is already in RUNNING order, it
        already names the shop and the chips, and it is the one place a keyboard
        user can move through the field in the order the race is actually in.

        The list stays visually hidden until something inside it takes focus,
        so tabbing to a driver shows a sighted keyboard user where they are
        instead of moving an invisible cursor around.
      */}
      {/*
        The arrows in words. Said whether or not they are drawn, because the box
        turns off a drawing, not a fact — see the note on `moves`.
      */}
      <p className="sr-only" aria-live="polite">
        {moves.length === 0 && movement.suppressed === 0
          ? 'Nothing changed position this turn.'
          : `This turn: ${moves.filter((m) => m.kind === 'gain').length} gained places, ` +
            `${moves.filter((m) => m.kind === 'loss').length} lost places, ` +
            `${moves.filter((m) => m.kind === 'trade').length} traded places` +
            (movement.suppressed > 0
              ? `, and ${movement.suppressed} moved within the bottom stack.`
              : '.')}
      </p>

      <ol className={onSelectDriver ? 'boards__order' : 'sr-only'} aria-label="Running order">
        {order.map((car) => {
          const shop = roster.get(car.card.driverId)?.team.name;
          const line =
            `${ordinal(car.position)} — car ${car.card.carNumber}, ${car.card.name}` +
            `${shop ? `, ${shop}` : car.card.teamId ? '' : ', independent'}` +
            `${car.chips > 0 ? `, ${car.chips} performance chip${car.chips === 1 ? '' : 's'}` : ''}` +
            `${car.lapsDown ? ', one lap down' : ''}`;
          return (
            <li key={car.card.driverId}>
              {onSelectDriver ? (
                <button
                  type="button"
                  className="boards__orderbtn"
                  onClick={() => onSelectDriver(car.card.driverId)}
                >
                  {line}
                  <span className="sr-only"> — open dossier</span>
                </button>
              ) : (
                line
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/**
 * THE ARROWS. D-EZ-58.
 *
 * One SVG the size of the whole table, laid over it. Everything inside is in
 * board units, exactly like the cards, so the layer scales with the stage and
 * never needs to know what `scale` is.
 *
 * ⚠ WHY A TRADE IS DRAWN TWICE. Two paths on the same `d`: a solid green one
 * carrying both heads, and a dashed red one over the top of it. The dashes let
 * the green through in the gaps, which is a barber-pole stripe made out of two
 * strokes rather than a gradient — and a gradient could not have put a
 * different colour on each head, which is the half of Howard's ask that says
 * which end each man arrived at.
 *
 * The heads are markers rather than hand-built polygons so that `orient="auto"`
 * turns them along the curve's own tangent. A bowed arm whose head points down
 * the straight line between its ends is subtly, permanently wrong.
 */
function MoveArrowLayer({ moves }: { moves: readonly MoveArrow[] }) {
  return (
    <svg
      className="movearrows"
      width={BOARD_W}
      height={BOARD_H}
      viewBox={`0 0 ${BOARD_W} ${BOARD_H}`}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <marker id="mv-gain" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5.5" markerHeight="5.5" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" className="movearrows__headgain" />
        </marker>
        <marker id="mv-loss" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5.5" markerHeight="5.5" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" className="movearrows__headloss" />
        </marker>
        {/* A trade's other head sits at the START of the path, so it faces back. */}
        <marker id="mv-loss-back" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5.5" markerHeight="5.5" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" className="movearrows__headloss" />
        </marker>
      </defs>
      {moves.map((move) => {
        const d = pathOf(move);
        if (move.kind === 'trade') {
          return (
            <g key={move.id} className="movearrow movearrow--trade">
              <path
                d={d}
                className="movearrow__arm movearrow__arm--gain"
                markerEnd="url(#mv-gain)"
                markerStart="url(#mv-loss-back)"
              />
              <path d={d} className="movearrow__arm movearrow__arm--stripe" />
            </g>
          );
        }
        return (
          <g key={move.id} className={`movearrow movearrow--${move.kind}`}>
            <path
              d={d}
              className={`movearrow__arm movearrow__arm--${move.kind}`}
              markerEnd={move.kind === 'gain' ? 'url(#mv-gain)' : 'url(#mv-loss)'}
            />
          </g>
        );
      })}
    </svg>
  );
}

function BoardHeading({ x, y, w, text }: { x: number; y: number; w: number; text: string }) {
  return (
    <h3
      className="board-heading"
      style={{ transform: `translate3d(${x}px, ${y}px, 0)`, width: w }}
    >
      {text}
    </h3>
  );
}

// ---------------------------------------------------------------------------
// One driver card
// ---------------------------------------------------------------------------

interface DriverCardFaceProps {
  car: Car;
  /** The shop's mark for this car's hood, or null. See `logoStore.ts`. */
  hoodLogo?: StoredLogo | null;
  /** The paint. `PRINTED_LIVERY` with `printed` set is the physical card. */
  livery: Livery;
  /** Draw the car as the card prints it: black ink, white number, no gloss. */
  printed?: boolean;
  /** Draw the card at this position instead of `car.position` (used for cars on their way out). */
  overridePosition?: number;
  collected?: boolean;
  highlighted?: boolean;
  tookTheLead?: boolean;
  out?: boolean;
  /** UI-008 — open this driver's dossier. Pointer only; see the note below. */
  onOpen?: (driverId: string) => void;
  /**
   * Drawn OFF the board — a copy on the dice table (7 Oct 2026). Sized as a
   * TOP-group card, laid out in normal flow instead of at a board slot.
   */
  standalone?: boolean;
}

export function DriverCardFace({
  car,
  hoodLogo = null,
  livery,
  printed = false,
  overridePosition,
  collected = false,
  highlighted = false,
  tookTheLead = false,
  out = false,
  onOpen,
  standalone = false,
}: DriverCardFaceProps) {
  const ct = useCiteText();
  // A standalone copy is always drawn as a TOP-group card (box 1's size), so a
  // retired car's out-of-range position never reaches the slot maths.
  const slot = slotFor(standalone ? 2 : overridePosition ?? car.position);
  const card = car.card;
  const held = carQualities(car);

  const style: CSSProperties = standalone
    ? { width: slot.w, height: slot.h }
    : {
        transform: `translate3d(${slot.x}px, ${slot.y}px, 0)`,
        width: slot.w,
        height: slot.h,
        zIndex: out ? 999 : slot.z + (highlighted ? 60 : 0),
      };

  const classes = [
    'dc',
    standalone ? 'dc--standalone' : '',
    `dc--${slot.group}`,
    slot.depth > 6 ? 'dc--packed' : '',
    collected ? 'is-collected' : '',
    highlighted ? 'is-lit' : '',
    tookTheLead ? 'is-new-leader' : '',
    out ? 'is-out' : '',
    car.lapsDown ? 'is-lapped' : '',
    onOpen ? 'is-openable' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article
      className={classes}
      style={style}
      // A dice-table copy must not answer to the board's own lookups.
      {...(standalone ? { 'data-table-driver': card.driverId } : { 'data-driver': card.driverId, 'data-position': slot.position })}
      aria-hidden="true"
    >
      {/*
        UI-008 — THE HIT AREA, AND WHY IT IS SHAPED LIKE THIS.

        The `<article>` above is the element the browser is tweening: it is
        keyed on driver id, it never unmounts while the driver is running, and
        its slot is nothing but a `translate3d`. Turning IT into a button — or
        wrapping it in one — would move the transform onto a different node and
        put the pass animation, which is the entire reason these boards exist,
        at risk for a click handler.

        So the button goes INSIDE, absolutely positioned over the card face. It
        rides the parent's transform for free, the tween is untouched, and the
        DOM order stays sorted by id.

        It is `tabIndex={-1}` on purpose. The card is `aria-hidden`, and a
        focusable control inside an aria-hidden subtree is a genuine
        accessibility fault, not a technicality — a keyboard would land on
        something no screen reader can announce. The keyboard door is the
        running-order list instead, which is in running order and says more.
      */}
      {onOpen && (
        <button
          type="button"
          className="dc__hit"
          tabIndex={-1}
          onClick={() => onOpen(card.driverId)}
          title={`${card.name} — open dossier`}
        />
      )}

      <div className="dc__car">
        <CarTopDown
          livery={livery}
          carNumber={card.carNumber}
          size={slot.group === 'leader' ? LEADER_CAR_W : CARD_CAR_W}
          printed={printed}
          hoodLogoUrl={hoodLogo?.url ?? null}
          hoodLogoLabel={hoodLogo?.label ?? null}
        />
        {card.triangle && <span className="dc__tri" title={ct('triangle car — a red 6 brings a PROBLEM (RB/xvi)')} />}
        {/*
          D-EZ-76 — the 2025 set's GREEN thumbs-up, which points the other way
          for the same reason it means the opposite: a failed REPAIR on a PROBLEM
          does not end this man's race. Drawn beside the warning triangle rather
          than in place of it, because they are two different symbols and a card
          could in principle print both.
        */}
        {card.dnfExempt && (
          <span
            className="dc__ok"
            title="green thumbs-up — a failed REPAIR on a PROBLEM does not put him out, but he can no longer challenge or defend (2025 set)"
          />
        )}
        {card.trait && (
          <span className="dc__trait" title={`${TRAITS_BY_CODE[card.trait].name} — ${TRAITS_BY_CODE[card.trait].text}`}>
            {card.trait}
          </span>
        )}
        {car.chips > 0 && (
          <span className="dc__chips">
            {Array.from({ length: car.chips }, (_, i) => (
              <i key={`chip-${i}`} className="dc__chip" />
            ))}
          </span>
        )}
      </div>
      <div className="dc__name">{card.name}</div>

      <div className="dc__pedals">
        <span className="dc__gas">
          {Array.from({ length: card.gasPedal }, (_, i) => (
            <i key={`gas-${i}`} className="dc__sq" />
          ))}
        </span>
        <span className="dc__brake">
          {Array.from({ length: card.brakePedal }, (_, i) => (
            <Burst key={`brk-${i}`} />
          ))}
        </span>
      </div>

      <div className="dc__grid">
        {QUALITY_GRID.map((q) => {
          const has = held.includes(q);
          const granted = car.tvQuality === q && !card.qualities.includes(q);
          return (
            <span
              key={q}
              className={`dc__q${has ? ' is-held' : ''}${granted ? ' is-tv' : ''}`}
            >
              {has ? q : ''}
              {has && isStarred(car, q) ? <b className="dc__qstar">★</b> : null}
            </span>
          );
        })}
      </div>

      <div className="dc__foot">
        <TrophyGrade trophy={card.trophy} grade={card.pitCrewGrade} />
        <span className="dc__stat">
          FP<b>{card.fastPit}</b>
        </span>
        <span className="dc__stat">
          ESC<b>{card.escape}</b>
        </span>
      </div>

      {car.lapsDown && <span className="dc__tag">lap down</span>}
      {out && <span className="dc__stamp">out</span>}
    </article>
  );
}

/**
 * The pit crew grade, printed the way the card prints it: inside a trophy, in
 * gold, silver or bronze, with the metal NAMED underneath — and bare, with no
 * trophy at all, when the crew has never won one. `trophy: null` is a real
 * value from Sagliano's table, not "unrated", so the letter must still be
 * there.
 *
 * The letter is drawn INSIDE the cup rather than beside it because that is what
 * the card does; the word underneath is the card's own belt-and-braces, and it
 * is the reason a colour-blind player can tell a gold crew from a bronze one at
 * a glance instead of by hovering.
 */
export function TrophyGrade({ trophy, grade }: { trophy: Trophy; grade: PitCrewGrade }) {
  if (!trophy) {
    return (
      <span className="dc__grade dc__grade--bare" title={`pit crew ${grade} — no trophy`}>
        {grade}
      </span>
    );
  }
  return (
    <span className={`dc__grade dc__grade--cup dc__grade--${trophy}`} title={`pit crew ${grade} — ${trophy} trophy`}>
      <svg className="dc__cup" viewBox="0 0 22 24" aria-hidden="true">
        {/* handles */}
        <path
          className="dc__cup-metal"
          d="M4.6 4.2 C0.6 4.6 0.9 11 5.2 11.6 M17.4 4.2 C21.4 4.6 21.1 11 16.8 11.6"
          fill="none"
          strokeWidth="1.7"
        />
        {/* bowl, stem and base */}
        <path
          className="dc__cup-metal dc__cup-fill"
          d="M4.4 2.4 H17.6 V9.6 C17.6 14.2 14.6 16.8 11 16.8 C7.4 16.8 4.4 14.2 4.4 9.6 Z"
          strokeWidth="1"
        />
        <path className="dc__cup-metal dc__cup-fill" d="M9.7 16.4 H12.3 V19.2 H9.7 Z" strokeWidth="1" />
        <path className="dc__cup-metal dc__cup-fill" d="M5.6 23 L7.1 19 H14.9 L16.4 23 Z" strokeWidth="1" />
        <text
          className="dc__cup-letter"
          x="11"
          y="9.1"
          textAnchor="middle"
          dominantBaseline="central"
        >
          {grade}
        </text>
      </svg>
      <b className="dc__gradeword">{trophy}</b>
    </span>
  );
}

/** The brake-pedal burst, drawn the way the printed card prints it. */
function Burst() {
  return (
    <svg className="dc__burst" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 0.5 14.9 8.3 22.6 5.2 18.4 12.2 24 17.4 16 17.9 17.2 24 12 19.6 6.8 24 8 17.9 0 17.4 5.6 12.2 1.4 5.2 9.1 8.3Z" />
    </svg>
  );
}
